import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ConfirmDialog } from './ConfirmDialog';

// The unsaved-changes guard. Freddi, 2026-10-08: "if i change anything in
// household modal without saving and try to navigate away i should see
// browser confirmation ... navigate to their bookings (household changes are
// lost) and i am not aware."
//
// Three doors out of an edit, one rule. A form REPORTS whether it is dirty;
// anything that would leave it — another household from the rail, a stay from
// the timeline, New booking, Edit owner, Back, Escape, the close button, Cancel
// — asks the guard first, and the guard either proceeds or puts one question
// on screen: Discard changes? Keep editing is the safe answer. The browser's
// own prompt covers the fourth door, a refresh or a closed tab, through
// `beforeunload` — which is all it can cover; it cannot see a click inside the
// page, which is why the first three need this.

export interface GuardApi {
  /** Run `proceed` now, or after the person has chosen to discard. */
  guard: (proceed: () => void) => void;
  /** A form says whether it holds unsaved edits, and what to call them. */
  report: (key: string, dirty: boolean, what?: string) => void;
  isDirty: () => boolean;
  /**
   * A SAVE IS NOT LEAVING. The close-first pattern closes the pane before the
   * write starts, through the same shell dismissal Cancel uses — and the
   * guard, still holding the edit just committed, asked "Discard changes?" on
   * a save (Freddi, 2026-10-08). Release clears every report so the dismissal
   * that follows a save, a delete or an archive goes through.
   */
  release: () => void;
}

const Ctx = createContext<GuardApi | null>(null);

/** The words for the question: "this household", "this booking", or the generic. Pure, so it is tested. */
export function dirtyDescription(whats: readonly string[]): string {
  const named = whats.filter((w) => w.trim());
  if (named.length === 0) return 'this record';
  if (named.length === 1) return named[0]!;
  return `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`;
}

export function UnsavedGuardProvider({ children }: { children: ReactNode }) {
  const dirty = useRef(new Map<string, string>());
  const [pending, setPending] = useState<{ proceed: () => void; what: string } | null>(null);

  const report = useCallback((key: string, isDirty: boolean, what = '') => {
    if (isDirty) dirty.current.set(key, what);
    else dirty.current.delete(key);
  }, []);
  const isDirty = useCallback(() => dirty.current.size > 0, []);
  const release = useCallback(() => dirty.current.clear(), []);
  const guard = useCallback((proceed: () => void) => {
    if (dirty.current.size === 0) {
      proceed();
      return;
    }
    setPending({ proceed, what: dirtyDescription([...dirty.current.values()]) });
  }, []);

  // The fourth door. Registered once; it asks the map at the moment of leaving.
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (dirty.current.size === 0) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, []);

  const api = useMemo<GuardApi>(() => ({ guard, report, isDirty, release }), [guard, report, isDirty, release]);
  return (
    <Ctx.Provider value={api}>
      {children}
      {pending && (
        <ConfirmDialog
          title="Discard changes?"
          message={`You have unsaved changes to ${pending.what}. Discard them, or keep editing.`}
          confirmLabel="Discard"
          cancelLabel="Keep editing"
          danger
          onCancel={() => setPending(null)}
          onConfirm={() => {
            dirty.current.clear();
            const p = pending;
            setPending(null);
            p.proceed();
          }}
        />
      )}
    </Ctx.Provider>
  );
}

const PASS: GuardApi = { guard: (p) => p(), report: () => {}, isDirty: () => false, release: () => {} };

/** Outside a provider the guard is a pass-through, so a pane used elsewhere is unchanged. */
export function useUnsavedGuard(): GuardApi {
  return useContext(Ctx) ?? PASS;
}

/** A form's one line: what it is, and whether it is dirty right now. Cleared on unmount. */
export function useReportDirty(key: string, isDirty: boolean, what: string): void {
  const api = useContext(Ctx);
  useEffect(() => {
    api?.report(key, isDirty, what);
  }, [api, key, isDirty, what]);
  useEffect(() => () => api?.report(key, false), [api, key]);
}

/**
 * THE ONE LINE A FORM WRITES. Hand it the draft — every field a save would
 * write, and nothing else — and it compares it, as a string, with the draft
 * at mount: different is dirty. `markSaved` makes the current draft the new
 * baseline after a keep-open save. Opening a section, focusing a field or a
 * derived value that is not in the draft is never a change, which is why the
 * form chooses what goes in rather than the hook watching everything.
 *
 * ONE TRAP: a value a PARENT sets in an effect after this form's first render
 * is not in its final state when the baseline is taken, so it would read as
 * an edit at once. Compare such a value to its saved source instead of
 * snapshotting it — see BookingForm's `statusChanged`.
 */
export function useDirtyDraft(key: string, what: string, draft: unknown): { dirty: boolean; markSaved: () => void; markSavedAs: (next: unknown) => void } {
  const sig = JSON.stringify(draft);
  const baseline = useRef(sig);
  const dirty = sig !== baseline.current;
  // DEV TRACE. When a draft first reads dirty, say which fields differ from the
  // baseline — a false positive here is otherwise invisible from the outside.
  const traced = useRef(false);
  if (import.meta.env.DEV && dirty && !traced.current) {
    traced.current = true;
    try {
      const a = JSON.parse(baseline.current) as Record<string, unknown>;
      const b = JSON.parse(sig) as Record<string, unknown>;
      const diff = Object.keys({ ...a, ...b }).filter((k) => JSON.stringify(a[k]) !== JSON.stringify(b[k]))
        .map((k) => ({ field: k, baseline: a[k], now: b[k] }));
      console.debug('[dirty] ' + key + ' ' + JSON.stringify(diff));
    } catch { console.debug('[dirty]', key, 'unparseable'); }
  } else if (!dirty) traced.current = false;
  useReportDirty(key, dirty, what);
  const markSaved = useCallback(() => {
    baseline.current = sig;
  }, [sig]);
  /** The draft about to be set is the saved one — for a change the server made, not the user (a date
   *  Patricia accepted beside the form). Set before the state lands, so no render reads it as dirty. */
  const markSavedAs = useCallback((next: unknown) => {
    baseline.current = JSON.stringify(next);
  }, []);
  return { dirty, markSaved, markSavedAs };
}
